import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAIProfile } from '../context/AIProfileContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuizPage = () => {
  const navigate = useNavigate();
  const { setAIProfile, clearProfile } = useAIProfile();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acknowledgment, setAcknowledgment] = useState('');
  const [softLeadCaptured, setSoftLeadCaptured] = useState(false);
  const [wantTextUpdates, setWantTextUpdates] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    email: '',
    phone: '',
    age_range: '',
    height: '',
    current_weight: '',
    goal_weight: '',
    timeline: '',
    prior_glp1: '',
    medication_interest: '',
    preference: '',
    biggest_challenge: '',
    budget: '',
    future_interests: []
  });

  // Track soft lead capture event
  const trackEvent = useCallback(async (eventType, metadata = {}) => {
    try {
      await axios.post(`${API}/events/track`, {
        event_type: eventType,
        source: 'quiz_page',
        metadata: {
          ...metadata,
          step: currentStep,
          email: formData.email || null,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      // Event tracking is non-critical
    }
  }, [currentStep, formData.email]);

  // Save partial lead when email is captured (for abandonment recovery)
  const savePartialLead = useCallback(async () => {
    if (!formData.email || softLeadCaptured) return;
    
    try {
      await axios.post(`${API}/partial-leads`, {
        first_name: formData.first_name,
        email: formData.email,
        phone: formData.phone || null,
        partial_data: formData,
        source: 'soft_lead_capture',
        step_reached: currentStep
      });
      setSoftLeadCaptured(true);
      trackEvent('soft_lead_capture_submitted', { has_phone: !!formData.phone });
    } catch (error) {
      // Partial lead save is non-critical
    }
  }, [formData, softLeadCaptured, currentStep, trackEvent]);

  // Track page visibility for abandonment detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && formData.email && !softLeadCaptured) {
        // User is leaving - try to save partial lead
        trackEvent('quiz_abandoned_after_capture', { step: currentStep });
        savePartialLead();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [formData.email, softLeadCaptured, currentStep, trackEvent, savePartialLead]);

  // Clear any existing profile when starting a new quiz
  useEffect(() => {
    clearProfile();
  }, [clearProfile]);

  const questions = [
    {
      id: 'first_name',
      label: 'What\'s your first name?',
      type: 'text',
      placeholder: 'Enter your first name'
    },
    {
      id: 'age_range',
      label: 'Your age range',
      type: 'select',
      options: ['18-25', '26-35', '36-45', '46-55', '56-65', '66+']
    },
    {
      id: 'height',
      label: 'Your height',
      type: 'text',
      placeholder: 'e.g., 5\'8"'
    },
    {
      id: 'current_weight',
      label: 'Current weight (lbs)',
      type: 'text',
      placeholder: 'e.g., 180'
    },
    {
      id: 'goal_weight',
      label: 'Goal weight (lbs)',
      type: 'text',
      placeholder: 'e.g., 150'
    },
    {
      id: 'timeline',
      label: 'Target timeline',
      type: 'select',
      options: ['1-3 months', '3-6 months', '6-12 months', '12+ months', 'No rush']
    },
    {
      id: 'prior_glp1',
      label: 'Have you used GLP-1 medications before?',
      type: 'select',
      options: ['Never', 'Yes, in the past', 'Currently using', 'Not sure what this is']
    },
    {
      id: 'medication_interest',
      label: 'Which medication interests you most?',
      type: 'select',
      options: ['Semaglutide', 'Tirzepatide', 'Not sure yet', 'Want to compare options']
    },
    {
      id: 'preference',
      label: 'Medication preference',
      type: 'select',
      options: ['Injection', 'Oral', 'Either works for me', 'Need guidance']
    },
    {
      id: 'biggest_challenge',
      label: 'What\'s your biggest challenge?',
      type: 'select',
      options: ['Appetite control', 'Cravings', 'Low energy', 'Consistency', 'Weight plateau', 'Post-pregnancy', 'Busy lifestyle']
    },
    // SOFT LEAD CAPTURE - Step 11 of 14 (after user is invested)
    {
      id: 'soft_lead_capture',
      label: 'Where should we send your AI forecast?',
      type: 'lead_capture',
      subtitle: "You're almost done — let's save your personalized forecast."
    },
    {
      id: 'budget',
      label: 'Budget comfort level',
      type: 'select',
      options: ['Starter ($150-250/month)', 'Moderate ($250-400/month)', 'Premium ($400+/month)', 'Need more info']
    },
    {
      id: 'future_interests',
      label: 'Future wellness interests (select all)',
      type: 'multi-select',
      options: ['NAD+ therapy', 'Hair support', 'Energy support', 'Men\'s wellness', 'Women\'s wellness', 'Not now']
    }
  ];

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  // Get encouraging progress message based on current step
  const getProgressMessage = () => {
    const percentage = Math.round(progress);
    
    // Special message for soft lead capture step
    if (currentQuestion?.id === 'soft_lead_capture') {
      return "You're almost done — let's save your personalized forecast.";
    }
    
    if (percentage <= 20) {
      return "Great start! Let's get to know you better.";
    } else if (percentage <= 40) {
      return "You're doing great! Keep going.";
    } else if (percentage <= 60) {
      return "Nice progress! We're halfway there.";
    } else if (percentage <= 80) {
      return "Almost there! Just a few more questions.";
    } else if (percentage < 100) {
      return "Final stretch! You're almost done.";
    } else {
      return "Perfect! Ready to see your forecast.";
    }
  };

  // Get acknowledgment message for specific answers
  const getAcknowledgment = (questionId, value) => {
    const acknowledgments = {
      biggest_challenge: {
        'Appetite control': "If appetite control has been difficult, you're definitely not alone. Many people find support with the right approach.",
        'Cravings': "Cravings can be tough to manage on your own. The good news is there are proven strategies that can help.",
        'Low energy': "Low energy can make everything harder. Finding the right support could make a big difference in your journey.",
        'Consistency': "Staying consistent is one of the biggest challenges. That's exactly where structured support can help.",
        'Weight plateau': "Hitting a plateau is frustrating, but it's often a sign you need a different approach. There are options that can help break through.",
        'Post-pregnancy': "Post-pregnancy weight loss has unique challenges. Many have found success with the right medical support.",
        'Busy lifestyle': "A busy lifestyle makes everything harder. The right plan should work with your schedule, not against it."
      },
      prior_glp1: {
        'Currently using': "Great! You already know how these medications work. Let's explore what could optimize your results.",
        'Yes, in the past': "Your past experience will help us find what works best for you moving forward.",
        'Never': "No problem! We'll make sure you understand all your options and what to expect."
      },
      timeline: {
        '1-3 months': "That's an ambitious but achievable timeline with the right support.",
        '3-6 months': "A 3-6 month timeline gives you room for sustainable results.",
        '6-12 months': "Taking a longer-term approach often leads to the most lasting results."
      }
    };

    return acknowledgments[questionId]?.[value] || '';
  };

  // Effect to show acknowledgment after certain answers
  useEffect(() => {
    if (currentQuestion && formData[currentQuestion.id]) {
      const ack = getAcknowledgment(currentQuestion.id, formData[currentQuestion.id]);
      if (ack) {
        setAcknowledgment(ack);
        // Clear acknowledgment after 5 seconds
        const timer = setTimeout(() => setAcknowledgment(''), 5000);
        return () => clearTimeout(timer);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, formData]);

  const handleNext = () => {
    setAcknowledgment(''); // Clear acknowledgment when moving forward
    
    // Track soft lead capture step shown
    if (questions[currentStep + 1]?.id === 'soft_lead_capture') {
      trackEvent('soft_lead_capture_shown');
    }
    
    // Save partial lead when moving past soft lead capture step
    if (currentQuestion?.id === 'soft_lead_capture' && formData.email) {
      savePartialLead();
    }
    
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleChange = (value) => {
    setFormData({
      ...formData,
      [currentQuestion.id]: value
    });
  };

  const handleMultiSelectChange = (option) => {
    const current = formData[currentQuestion.id] || [];
    if (current.includes(option)) {
      setFormData({
        ...formData,
        [currentQuestion.id]: current.filter(item => item !== option)
      });
    } else {
      setFormData({
        ...formData,
        [currentQuestion.id]: [...current, option]
      });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/leads`, {
        quiz_responses: formData
      });
      
      // Store AI Profile in context
      if (response.data.ai_profile) {
        setAIProfile(response.data.ai_profile);
      }
      
      navigate(`/results/${response.data.lead_id}`, {
        state: { 
          forecastData: response.data,
          quizResponses: formData,
          aiProfile: response.data.ai_profile
        }
      });
    } catch (error) {
      // Quiz submission error handled by UI state
      // Even on error, try to proceed (backend should handle failures gracefully)
      // Show friendly message and retry once
      try {
        const retryResponse = await axios.post(`${API}/leads`, {
          quiz_responses: formData
        });
        
        // Store AI Profile in context
        if (retryResponse.data.ai_profile) {
          setAIProfile(retryResponse.data.ai_profile);
        }
        
        navigate(`/results/${retryResponse.data.lead_id}`, {
          state: { 
            forecastData: retryResponse.data,
            quizResponses: formData,
            aiProfile: retryResponse.data.ai_profile
          }
        });
      } catch (retryError) {
        // If both attempts fail, show user-friendly message (should be rare with backend fallback)
        alert('We\'re experiencing high demand. Please try again in a moment.');
        setIsSubmitting(false);
      }
    }
  };

  const isCurrentStepValid = () => {
    // Special validation for soft lead capture step
    if (currentQuestion.type === 'lead_capture') {
      // Email is required, phone is optional
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return formData.email && emailRegex.test(formData.email);
    }
    
    const value = formData[currentQuestion.id];
    if (currentQuestion.type === 'multi-select') {
      return value && value.length > 0;
    }
    return value && value.trim() !== '';
  };

  return (
    <div className="min-h-screen bg-[#020817] text-foreground flex items-center justify-center p-6 pb-safe">
      <div className="w-full max-w-2xl py-8 mb-24">
        {/* Progress Bar with Encouraging Message */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm text-slate-400">Question {currentStep + 1} of {questions.length}</span>
            <span className="text-sm text-[#22D3EE] font-medium">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="quiz-progress" />
          
          {/* Encouraging Progress Text */}
          <motion.p
            key={Math.floor(progress / 20)}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="text-sm text-cyan-400/80 mt-3 text-center font-medium"
          >
            {getProgressMessage()}
          </motion.p>
        </div>

        {/* Acknowledgment Message */}
        <AnimatePresence>
          {acknowledgment && (
            <motion.div
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-6 p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20 backdrop-blur-sm"
              data-testid="acknowledgment-message"
            >
              <p className="text-sm text-cyan-300/90 leading-relaxed">
                💙 {acknowledgment}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl"
            data-testid="quiz-question-card"
          >
            {/* Soft Lead Capture Step - Special UI */}
            {currentQuestion.type === 'lead_capture' ? (
              <div className="space-y-6" data-testid="soft-lead-capture-step">
                {/* Header with icon */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500/30 to-emerald-500/20 flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(34,211,238,0.3)]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <Label className="text-2xl sm:text-3xl font-heading font-bold mb-3 block text-white">
                    {currentQuestion.label}
                  </Label>
                  <p className="text-cyan-400/80 text-sm font-medium">
                    {currentQuestion.subtitle}
                  </p>
                </div>

                {/* Email Field (Required) */}
                <div className="space-y-2">
                  <Label className="text-sm text-slate-400 flex items-center gap-2">
                    Email Address
                    <span className="text-cyan-500 text-xs font-medium">Required</span>
                  </Label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-slate-950/50 border-2 border-slate-800 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 rounded-xl h-14 px-4 text-base transition-all duration-300"
                    data-testid="soft-lead-email"
                  />
                </div>

                {/* Phone Field (Optional) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-slate-400">
                      Phone Number
                      <span className="text-slate-500 text-xs ml-2">Optional</span>
                    </Label>
                    <button
                      type="button"
                      onClick={() => setWantTextUpdates(!wantTextUpdates)}
                      className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full transition-all ${
                        wantTextUpdates 
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                          : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <span className={`w-3 h-3 rounded-full transition-all ${wantTextUpdates ? 'bg-emerald-400' : 'bg-slate-600'}`}></span>
                      Want text updates?
                    </button>
                  </div>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="bg-slate-950/50 border-2 border-slate-800 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 rounded-xl h-14 px-4 text-base transition-all duration-300"
                    data-testid="soft-lead-phone"
                  />
                </div>

                {/* Trust Text */}
                <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      We'll use this to send your AI results and next steps. <span className="text-slate-500">No spam, ever.</span>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Label className="text-xl sm:text-2xl font-heading font-bold mb-6 sm:mb-8 block">
                  {currentQuestion.label}
                </Label>

                {currentQuestion.type === 'text' || currentQuestion.type === 'email' || currentQuestion.type === 'tel' ? (
                  <Input
                    type={currentQuestion.type}
                    placeholder={currentQuestion.placeholder}
                    value={formData[currentQuestion.id]}
                    onChange={(e) => handleChange(e.target.value)}
                    className="bg-slate-950/50 border-2 border-slate-800 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 rounded-xl h-14 sm:h-12 px-4 text-base transition-all duration-300"
                    data-testid={`quiz-input-${currentQuestion.id}`}
                  />
                ) : currentQuestion.type === 'select' ? (
                  <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
                    {currentQuestion.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleChange(option)}
                        className={`w-full text-left px-4 sm:px-6 py-4 sm:py-5 rounded-xl border-2 transition-all duration-300 ${
                          formData[currentQuestion.id] === option
                            ? 'border-[#22D3EE] bg-[#22D3EE]/15 text-white shadow-[0_0_20px_rgba(34,211,238,0.3)] scale-[1.02] font-medium'
                            : 'border-slate-800 bg-slate-950/30 text-slate-300 hover:border-slate-700 hover:bg-slate-900/40 active:scale-[0.98]'
                        }`}
                        data-testid={`quiz-option-${idx}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : currentQuestion.type === 'multi-select' ? (
                  <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
                    {currentQuestion.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleMultiSelectChange(option)}
                        className={`w-full text-left px-4 sm:px-6 py-4 sm:py-5 rounded-xl border-2 transition-all duration-300 ${
                          formData[currentQuestion.id]?.includes(option)
                            ? 'border-[#22D3EE] bg-[#22D3EE]/15 text-white shadow-[0_0_20px_rgba(34,211,238,0.3)] scale-[1.02] font-medium'
                            : 'border-slate-800 bg-slate-950/30 text-slate-300 hover:border-slate-700 hover:bg-slate-900/40 active:scale-[0.98]'
                        }`}
                        data-testid={`quiz-multi-option-${idx}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons - Sticky at bottom on mobile with enhanced visibility */}
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#020817] via-[#020817]/98 to-[#020817]/80 backdrop-blur-xl border-t border-cyan-500/20 p-4 sm:p-5 pb-safe z-[60] shadow-[0_-4px_20px_rgba(0,0,0,0.5)] sm:relative sm:bg-transparent sm:backdrop-blur-none sm:border-0 sm:shadow-none sm:p-0 sm:mt-8">
          <div className="flex justify-between items-center max-w-2xl mx-auto gap-4">
            <Button
              onClick={handleBack}
              disabled={currentStep === 0}
              variant="outline"
              className="rounded-full px-5 sm:px-6 h-12 sm:h-11 text-base font-medium border-slate-700 hover:border-slate-600 disabled:opacity-40"
              data-testid="quiz-back-btn"
            >
              <ChevronLeft className="mr-1 sm:mr-2 h-5 w-5 sm:h-4 sm:w-4" />
              <span>Back</span>
            </Button>

            <Button
              onClick={handleNext}
              disabled={!isCurrentStepValid() || isSubmitting}
              className={`rounded-full px-8 sm:px-10 h-14 sm:h-12 text-base sm:text-lg font-bold transition-all duration-300 flex-1 max-w-xs sm:flex-initial ${
                !isCurrentStepValid() || isSubmitting
                  ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                  : 'bg-primary text-primary-foreground hover:bg-cyan-300 shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:shadow-[0_0_40px_rgba(34,211,238,0.7)] hover:scale-105 active:scale-95'
              }`}
              data-testid="quiz-next-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  <span className="hidden sm:inline">Generating Forecast...</span>
                  <span className="sm:hidden">Generating...</span>
                </>
              ) : currentStep === questions.length - 1 ? (
                <>
                  <span className="hidden sm:inline">Get My Forecast</span>
                  <span className="sm:hidden">Get Forecast</span>
                </>
              ) : (
                <>
                  <span>Next</span>
                  <ChevronRight className="ml-1 sm:ml-2 h-5 w-5 sm:h-4 sm:w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;